import { takeLatest, call, put } from 'redux-saga/effects';
import { 
  FETCH_DATA_TAG_REPORTS,
  DOWNLOAD_DATA_TAG_REPORT,
  VIEW_DATA_TAG_REPORT,
  UPLOAD_DATA_TAG_REPORT,
  UPLOAD_DATA_TAG_REPORT_SUCCESS,
  UPLOAD_DATA_TAG_REPORT_FAILURE,
  SET_DATA_TAG_REPORTS_LOADER,
  SET_DATA_TAG_ALL_REPORTS,
  SET_DATA_TAG_DOWNLOAD_LOADER,
  SET_DATA_TAG_SNACKBAR,
  DATA_TAG_REPORTS_API_URL,
  DATA_TAG_UPLOAD_API_URL,
} from "../../features/Data Tag/DataTagConstants";
import { getAxiosHeaders, getService } from '../../library/RestAPI';
import axios from 'axios';


function* fetchDataTagReports(action) {
  yield put({ type: SET_DATA_TAG_REPORTS_LOADER, payload: true });
  
  const options = {
    method: "GET",
    url: DATA_TAG_REPORTS_API_URL + (action.payload?.reportType ? `?type=${action.payload.reportType}` : ""),
    headers: getAxiosHeaders(true),
  };

  try {
    const res = yield getService(options);
    if (res && res.status === 200 && res.data.status === "success") {
      const files = res.data.files || [];
      
      // Store all files for client-side filtering
      yield put({ type: SET_DATA_TAG_ALL_REPORTS, payload: files });
      yield put({ type: SET_DATA_TAG_REPORTS_LOADER, payload: false });
    } else {
      yield put({ type: SET_DATA_TAG_ALL_REPORTS, payload: [] });
      yield put({ type: SET_DATA_TAG_REPORTS_LOADER, payload: false });
      yield put({ 
        type: SET_DATA_TAG_SNACKBAR, 
        payload: { status: "error", message: "Failed to fetch reports" } 
      });
    }
  } catch (error) {
    console.error('Error fetching Data Tag reports:', error);
    yield put({ type: SET_DATA_TAG_ALL_REPORTS, payload: [] });
    yield put({ type: SET_DATA_TAG_REPORTS_LOADER, payload: false });
    yield put({ 
      type: SET_DATA_TAG_SNACKBAR, 
      payload: { status: "error", message: "Error fetching reports" } 
    });
  }
}

function* downloadDataTagReport(action) {
  const { downloadUrl, fileName } = action.payload;
  yield put({ type: SET_DATA_TAG_DOWNLOAD_LOADER, payload: true });
  
  try {
    const options = {
      method: "GET",
      url: downloadUrl,
      headers: getAxiosHeaders(true),
      responseType: 'blob'
    };
    
    const response = yield getService(options);
    
    if (response && response.data) {
      // Get content type from response headers
      const contentType = response.headers['content-type'] || 'application/pdf';
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);
      
      yield put({ type: SET_DATA_TAG_DOWNLOAD_LOADER, payload: false });
      yield put({ 
        type: SET_DATA_TAG_SNACKBAR, 
        payload: { status: "success", message: "File downloaded successfully" } 
      });
    } else {
      yield put({ type: SET_DATA_TAG_DOWNLOAD_LOADER, payload: false });
      yield put({ 
        type: SET_DATA_TAG_SNACKBAR, 
        payload: { status: "error", message: "Failed to download file" } 
      });
    }
  } catch (error) {
    console.error('Error downloading file:', error);
    yield put({ type: SET_DATA_TAG_DOWNLOAD_LOADER, payload: false });
    yield put({ 
      type: SET_DATA_TAG_SNACKBAR, 
      payload: { status: "error", message: "Failed to download file" } 
    });
  }
}

function* viewDataTagReport(action) {
  const { viewUrl } = action.payload;
  yield put({ type: SET_DATA_TAG_DOWNLOAD_LOADER, payload: true });
  
  try {
    const options = {
      method: "GET",
      url: viewUrl,
      headers: getAxiosHeaders(true),
      responseType: 'blob'
    };
    
    const response = yield getService(options);
    
    if (response && response.data) {
      // Get content type from response headers
      const contentType = response.headers['content-type'] || 'application/pdf';
      
      // Create a blob URL and open in new tab
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Open the blob URL in a new tab
      window.open(blobUrl, '_blank');
      
      // Note: We don't revoke the blob URL immediately because it needs to stay valid
      // while the new tab is loading. The browser will clean it up eventually.
      
      yield put({ type: SET_DATA_TAG_DOWNLOAD_LOADER, payload: false });
    } else {
      yield put({ type: SET_DATA_TAG_DOWNLOAD_LOADER, payload: false });
      yield put({ 
        type: SET_DATA_TAG_SNACKBAR, 
        payload: { status: "error", message: "Failed to view file" } 
      });
    }
  } catch (error) {
    console.error('Error viewing file:', error);
    yield put({ type: SET_DATA_TAG_DOWNLOAD_LOADER, payload: false });
    yield put({ 
      type: SET_DATA_TAG_SNACKBAR, 
      payload: { status: "error", message: "Failed to view file" } 
    });
  }
}

function* uploadDataTagReport(action) {
  const { file, reportName, description, section, reportPeriodStart, reportPeriodEnd, expiryDate, reportType } = action.payload;
  
  try {
    // Create FormData for multipart/form-data upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('reportName', reportName);
    if (description) {
      formData.append('description', description);
    }
    if (section) {
      formData.append('sections', section);
    }
    formData.append('reportPeriodStart', reportPeriodStart);
    formData.append('reportPeriodEnd', reportPeriodEnd);
    formData.append('expiryDate', expiryDate);
    formData.append('type', reportType);
    // Get auth headers
    const headers = getAxiosHeaders(true);
    
    // Use axios directly for upload progress tracking
    const response = yield call(
      axios.post,
      DATA_TAG_UPLOAD_API_URL,
      formData,
      {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Dispatch progress update via a non-blocking call
          window.dispatchEvent(new CustomEvent('uploadProgress', { detail: percentCompleted }));
        },
      }
    );

    if (response && (response.status === 200 || response.status === 201)) {
      yield put({ 
        type: UPLOAD_DATA_TAG_REPORT_SUCCESS, 
        payload: response.data.message || "Report uploaded successfully!" 
      });
      // Refresh the reports list after successful upload
      yield put({ type: FETCH_DATA_TAG_REPORTS, payload: { reportType: reportType }  });
    } else {
      yield put({ 
        type: UPLOAD_DATA_TAG_REPORT_FAILURE, 
        payload: "Upload failed. Please try again." 
      });
    }
  } catch (error) {
    console.error('Error uploading report:', error);
    const errorMessage = error.response?.data?.message || error.message || "Failed to upload report";
    yield put({ 
      type: UPLOAD_DATA_TAG_REPORT_FAILURE, 
      payload: errorMessage 
    });
  }
}

export function* DataTagReportsSaga() {
  yield takeLatest(FETCH_DATA_TAG_REPORTS, fetchDataTagReports);
  yield takeLatest(DOWNLOAD_DATA_TAG_REPORT, downloadDataTagReport);
  yield takeLatest(VIEW_DATA_TAG_REPORT, viewDataTagReport);
  yield takeLatest(UPLOAD_DATA_TAG_REPORT, uploadDataTagReport);
}
